import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { HeadlineOverlay } from '@/store/videoFeatures/types/headline-overlay';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PitchRetentionCell } from '@/components/metrics/pitch/PitchRetentionCell';
import type { TestPitchMetric } from '@/types/pitch';
import { PitchSelector } from '@/components/metrics/pitch/PitchSelector';
import { byPitchOptions, usePitchSelection } from '@/components/metrics/pitch/pitchSelection';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { BarChart3, Star, Info, Loader2 } from 'lucide-react';
import { HeadlineTestMetrics } from '@/services/headlineTestService';
import { PeriodPicker } from '@/components/ui/period-picker';
import { buildPresetRange } from '@/utils/period-presets';

interface HeadlineMetricsOverviewProps {
  headlineOverlay: HeadlineOverlay;
  headlineTestId?: string;
  metricsData?: HeadlineTestMetrics[] | null;
  isLoadingMetrics?: boolean;
  startDate?: Date;
  endDate?: Date;
  onStartDateChange?: (date: Date) => void;
  onEndDateChange?: (date: Date) => void;
  onRefreshMetrics?: () => void;
}

interface HeadlineVariationMetric {
  variationName: string;
  visualizacoes: number;
  visualizacoesUnicas: number;
  plays: number;
  playsUnicos: number;
  playRate: number;
  /** Pitches do vídeo ORIGINAL com a retenção desta variação em cada um. */
  pitches: TestPitchMetric[];
  pitchVideoId: string | null;
  videoDuration: number | null;
  cliquesNoBotao: number;
  conversoes: number;
  taxaConversao: number;
  receita: number;
  engajamento: number;
  isChampion: boolean;
}

const TableHeadWithTooltip = ({ children, tooltip }: { children: React.ReactNode; tooltip: string }) => { useLanguage(); return ((
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger className="flex items-center gap-1 cursor-help">
        {children}
        <Info className="h-3 w-3 text-[#9CA3AF]" />
      </TooltipTrigger>
      <TooltipContent>
        <p className="max-w-xs text-xs">{tooltip}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
)); };

export const HeadlineMetricsOverview: React.FC<HeadlineMetricsOverviewProps> = ({
  headlineOverlay,
  headlineTestId,
  metricsData,
  isLoadingMetrics,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onRefreshMetrics,
}) => {
  useLanguage();
  const isTestEnabled = headlineOverlay.abTestConfig?.enabled;

  // Fallback interno de datas só para o caso do pai não passar (retrocompatibilidade).
  const [internalRange, setInternalRange] = useState(() =>
    buildPresetRange('last30Days', new Date())
  );
  const effectiveStart = startDate ?? internalRange.from;
  const effectiveEnd = endDate ?? internalRange.to;
  const handleRangeChange = (range: { from: Date; to: Date }) => {
    setInternalRange(range);
    onStartDateChange?.(range.from);
    onEndDateChange?.(range.to);
  };
  const isLoading = !!isLoadingMetrics;

  // Mapeamento dos dados brutos da API para o formato da tabela, feito via
  // useMemo para não recalcular em cada render.
  const metrics: HeadlineVariationMetric[] = useMemo(() => {
    // Aceita tanto array puro quanto wrapper `{ data: [...] }` — defensivo
    // contra mudanças na shape da resposta.
    const rows: any[] = Array.isArray(metricsData)
      ? metricsData
      : Array.isArray((metricsData as any)?.data)
        ? (metricsData as any).data
        : [];

    if (rows.length === 0) return [];

    const toNumber = (value: any): number => {
      if (typeof value === 'number') return value;
      if (typeof value === 'string') {
        const parsed = parseFloat(value);
        return isNaN(parsed) ? 0 : parsed;
      }
      return 0;
    };

    const mapped = rows
      .map((item, index) => {
        const totais = item?.metric?.totais || {};
        const headlineTest = item?.headlineTest || {};
        return {
          variationName: String(headlineTest.nomeVideo || `Variação ${index + 1}`),
          visualizacoes: toNumber(totais.visualizacoes),
          visualizacoesUnicas: toNumber(totais.visualizacoesUnicas),
          plays: toNumber(totais.plays),
          playsUnicos: toNumber(totais.playsUnicos),
          playRate: toNumber(totais.taxareproducao),
          // Raiz do retorno de MetricService.execute (não `totais.pitch`, que
          // vem sempre vazio). Os pitches são os do vídeo ORIGINAL.
          pitches: Array.isArray(item?.metric?.pitches) ? item.metric.pitches : [],
          pitchVideoId: item?.metric?.pitchVideoId ?? null,
          videoDuration: item?.metric?.videoDuration ?? null,
          cliquesNoBotao: toNumber(totais.clicks),
          conversoes: toNumber(totais.conversoes || 0),
          taxaConversao: toNumber(totais.taxaConversao || 0),
          receita: toNumber(totais.receita || 0),
          engajamento: toNumber(totais.engajamento),
          isChampion: false,
        };
      })
      .sort((a, b) => b.playRate - a.playRate);

    if (mapped.length > 0) mapped[0].isChampion = true;
    return mapped;
  }, [metricsData]);

  // Seletor de pitch (só aparece com 2+ pitches). As variações compartilham os
  // pitches do vídeo original, então as opções saem da primeira linha.
  const pitchOptions = byPitchOptions(metrics[0]?.pitches ?? []);
  const [pitchId, setPitchId] = usePitchSelection(pitchOptions);

  if (!isTestEnabled) {
    return (
      <div className="text-center py-12">
        <BarChart3 className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium mb-2">{translateInterfaceText("Nenhum teste ativo")}</h3>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          {translateInterfaceText("💡 Dica: Configure um teste na aba 'Teste A/B' para visualizar métricas comparativas por variação.")}</p>
      </div>
    );
  }

  // Sem headlineTestId, não podemos buscar métricas
  if (!headlineTestId) {
    return (
      <div className="text-center py-12">
        <BarChart3 className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium mb-2">{translateInterfaceText("Teste não iniciado")}</h3>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          {translateInterfaceText("Inicie o teste na aba 'Teste A/B' para começar a coletar métricas.")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filtro de Data */}
      <Card className="border-[#E6E6E6] rounded-xl shadow-[0px_2px_6px_rgba(0,0,0,0.06)]">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <PeriodPicker
              value={{ from: effectiveStart, to: effectiveEnd }}
              onChange={handleRangeChange}
              className="md:min-w-[240px]"
            />

            <Button
              onClick={onRefreshMetrics}
              disabled={isLoading || !onRefreshMetrics}
              size="sm"
              className="ml-auto"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {translateInterfaceText("Carregando...")}</>
              ) : (
                translateInterfaceText("Atualizar")
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Estado de Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-3 text-muted-foreground">{translateInterfaceText("Carregando métricas...")}</span>
        </div>
      )}

      {/* Mensagem quando não há métricas */}
      {!isLoading && metrics.length === 0 && (
        <div className="text-center py-12">
          <BarChart3 className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">{translateInterfaceText("Nenhuma métrica disponível")}</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            {translateInterfaceText("Não há métricas para o período selecionado. Aguarde a coleta de dados ou ajuste o período.")}</p>
        </div>
      )}

      {/* Tabela Detalhada */}
      {!isLoading && metrics.length > 0 && (
        <Card className="border-[#E6E6E6] rounded-xl shadow-[0px_2px_6px_rgba(0,0,0,0.06)]">
        <CardHeader className="bg-[#F7F9FC] border-b border-[#E6E6E6] py-3">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-sm font-semibold text-[#1C1C1E]">
              {translateInterfaceText("Comparativo por Variação")}</CardTitle>
            <PitchSelector options={pitchOptions} value={pitchId} onChange={setPitchId} />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F7F9FC] hover:bg-[#F7F9FC]">
                  <TableHead className="text-xs font-semibold text-[#6F6F75] whitespace-nowrap">{translateInterfaceText("Variação")}</TableHead>
                  <TableHead className="text-xs font-semibold text-[#6F6F75] text-right whitespace-nowrap">
                    <TableHeadWithTooltip tooltip={translateInterfaceText("Percentual médio de retenção do vídeo assistido")}>
                      {translateInterfaceText("Engajamento")}</TableHeadWithTooltip>
                  </TableHead>
                  <TableHead className="text-xs font-semibold text-[#6F6F75] text-right whitespace-nowrap">
                    <TableHeadWithTooltip tooltip={translateInterfaceText("Total de visualizações do vídeo")}>
                      {translateInterfaceText("Visualizações")}</TableHeadWithTooltip>
                  </TableHead>
                  <TableHead className="text-xs font-semibold text-[#6F6F75] text-right whitespace-nowrap">
                    <TableHeadWithTooltip tooltip={translateInterfaceText("Visualizações únicas (por usuário)")}>
                      {translateInterfaceText("Vis. Únicas")}</TableHeadWithTooltip>
                  </TableHead>
                  <TableHead className="text-xs font-semibold text-[#6F6F75] text-right whitespace-nowrap">{translateInterfaceText("Plays")}</TableHead>
                  <TableHead className="text-xs font-semibold text-[#6F6F75] text-right whitespace-nowrap">
                    <TableHeadWithTooltip tooltip={translateInterfaceText("Plays únicos por usuário")}>
                      {translateInterfaceText("Plays Únicos")}</TableHeadWithTooltip>
                  </TableHead>
                  <TableHead className="text-xs font-semibold text-[#6F6F75] text-right whitespace-nowrap">
                    <TableHeadWithTooltip tooltip={translateInterfaceText("Percentual de visitantes que iniciaram o vídeo")}>
                      {translateInterfaceText("Play Rate")}</TableHeadWithTooltip>
                  </TableHead>
                  <TableHead className="text-xs font-semibold text-[#6F6F75] text-right whitespace-nowrap">
                    <TableHeadWithTooltip tooltip={translateInterfaceText("Percentual de espectadores que ainda assistia no momento do pitch")}>
                      {translateInterfaceText("Retenção no Pitch")}</TableHeadWithTooltip>
                  </TableHead>
                  <TableHead className="text-xs font-semibold text-[#6F6F75] text-right whitespace-nowrap">{translateInterfaceText("C. no Botão")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {metrics.map((metric, index) => (
                  <TableRow 
                    key={index}
                    className={cn(
                      metric.isChampion 
                        ? 'bg-[#F0F7FF] border-l-2 border-l-[#1F6BFF] hover:bg-[#E8F4FF]' 
                        : index % 2 === 0 ? 'bg-white hover:bg-[#F9FAFB]' : 'bg-[#FAFBFC] hover:bg-[#F9FAFB]'
                    )}
                  >
                    <TableCell className="font-medium text-[#1C1C1E]">
                      <div className="flex items-center gap-2">
                        <span className="truncate max-w-[150px]">{metric.variationName}</span>
                        {metric.isChampion && (
                          <Star className="h-3 w-3 text-[#1F6BFF] fill-[#1F6BFF] flex-shrink-0" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right text-[#1C1C1E]">
                      {typeof metric.engajamento === 'number' ? metric.engajamento.toFixed(1) : '0.0'}%
                    </TableCell>
                    <TableCell className="text-right text-[#1C1C1E]">
                      {typeof metric.visualizacoes === 'number' ? metric.visualizacoes : 0}
                    </TableCell>
                    <TableCell className="text-right text-[#1C1C1E]">
                      {typeof metric.visualizacoesUnicas === 'number' ? metric.visualizacoesUnicas : 0}
                    </TableCell>
                    <TableCell className="text-right text-[#1C1C1E]">
                      {typeof metric.plays === 'number' ? metric.plays : 0}
                    </TableCell>
                    <TableCell className="text-right text-[#1C1C1E]">
                      {typeof metric.playsUnicos === 'number' ? metric.playsUnicos : 0}
                    </TableCell>
                    <TableCell className="text-right text-[#1C1C1E] whitespace-nowrap">
                      {typeof metric.playRate === 'number' ? metric.playRate.toFixed(1) : '0.0'}%
                    </TableCell>
                    <TableCell className="text-right text-[#1C1C1E] whitespace-nowrap">
                      <PitchRetentionCell
                        pitch={metric.pitches.find((p) => p.id === pitchId) ?? null}
                        videoPitches={metric.pitches}
                        pitchVideoId={metric.pitchVideoId}
                        durationInSeconds={metric.videoDuration}
                        onSaved={onRefreshMetrics}
                      />
                    </TableCell>
                    <TableCell className="text-right text-[#1C1C1E] whitespace-nowrap">
                      {typeof metric.cliquesNoBotao === 'number' ? metric.cliquesNoBotao : 0}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
      )}
    </div>
  );
};
